import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Text,
  Spinner,
  Input,
  InputGroup,
  InputLeftElement,
  Badge,
} from '@chakra-ui/react'
import { useClientesQuery } from '../hooks/useClientesQuery'
import { format } from 'date-fns'
import { FaSearch } from 'react-icons/fa'
import { useDebounce } from '../../../../hooks/useDebounce'

export const ClientesModal = ({ isOpen, onClose }) => {
  const { data, isLoading, error } = useClientesQuery()
  const [searchTerm, setSearchTerm] = useState('')
  // Aplicando debounce ao termo de busca para evitar travamentos
  const debouncedSearchTerm = useDebounce(searchTerm, 300)
  const [filteredClientes, setFilteredClientes] = useState([])

  // Filtrar clientes com debounce para melhorar performance
  useEffect(() => {
    if (!data) return

    const term = debouncedSearchTerm.toLowerCase()
    const filtered = data.filter((cliente) => {
      return cliente.nome_cliente?.toLowerCase().includes(term) || cliente.empresa?.toLowerCase().includes(term) || cliente.celular?.includes(term)
    })

    setFilteredClientes(filtered)
  }, [debouncedSearchTerm, data])

  // Formatação da data para DD/MM/YYYY HH:MM
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm')
    } catch (e) {
      return dateString
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent maxW={{ base: '90vw', md: '80vw' }}>
        <ModalHeader bg="#8CC63F" color="white">
          Clientes Cadastrados
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody p={4}>
          <InputGroup mb={4}>
            <InputLeftElement pointerEvents="none">
              <FaSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Buscar por nome, empresa ou telefone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              bg="white"
              border="1px solid #ddd"
            />
          </InputGroup>

          {isLoading ? (
            <Box textAlign="center" py={10}>
              <Spinner size="xl" color="#8CC63F" />
            </Box>
          ) : error ? (
            <Box textAlign="center" py={10}>
              <Text color="red.500">Erro ao carregar os clientes.</Text>
            </Box>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead bg="#f4f4f4">
                  <Tr>
                    <Th>Nome Cliente</Th>
                    <Th>Empresa</Th>
                    <Th>Data</Th>
                    <Th>Vendedor</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredClientes?.length === 0 ? (
                    <Tr>
                      <Td colSpan={5} textAlign="center" py={4}>
                        {debouncedSearchTerm ? (
                          <Text>
                            Nenhum cliente encontrado com o termo "<b>{debouncedSearchTerm}</b>".
                          </Text>
                        ) : (
                          <Text>Nenhum cliente cadastrado.</Text>
                        )}
                      </Td>
                    </Tr>
                  ) : (
                    filteredClientes?.map((cliente) => (
                      <Tr key={cliente.id} _hover={{ bg: '#f9f9f9' }}>
                        <Td>{cliente.nome_cliente}</Td>
                        <Td>{cliente.empresa || '-'}</Td>
                        <Td>{formatDate(cliente.data_cadastro)}</Td>
                        <Td>
                          {cliente.nome_vendedor || (
                            <Badge colorScheme="gray" fontSize="0.8em">
                              Não atribuído
                            </Badge>
                          )}
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="green" bg="#8CC63F" mr={3} onClick={onClose}>
            Fechar
          </Button>
          <Text fontSize="sm" color="gray.500">
            Total: {filteredClientes?.length || 0} cliente(s)
          </Text>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
